import { useRef, useState } from 'react';
import { apiJson } from '../api/client.js';
import { BUTTON_PRESETS, DEFAULT_BRANDING_COLORS } from '../branding/buttonPresets.js';
import { ButtonPresetPreview } from './ButtonPresetPreview.jsx';

/**
 * @param {{
 *   form: Record<string, string>;
 *   setForm: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
 *   logoUrl?: string | null;
 *   onLogoChange: (url: string | null) => void;
 * }} props
 */
export function PortalBrandingSection({ form, setForm, logoUrl, onLogoChange }) {
  const fileRef = useRef(null);
  const [logoMsg, setLogoMsg] = useState('');
  const [logoError, setLogoError] = useState('');
  const [uploading, setUploading] = useState(false);

  const setColor = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const uploadLogo = async (file) => {
    if (!file) {
      return;
    }
    setUploading(true);
    setLogoError('');
    setLogoMsg('');
    try {
      const body = new FormData();
      body.append('logo', file);
      const result = await apiJson('/api/admin/organization/logo', { method: 'POST', body });
      onLogoChange(result.logo_url);
      setLogoMsg('Logo uploaded. It is used in the header and as the browser favicon.');
    } catch (err) {
      setLogoError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) {
        fileRef.current.value = '';
      }
    }
  };

  const removeLogo = async () => {
    setUploading(true);
    setLogoError('');
    setLogoMsg('');
    try {
      await apiJson('/api/admin/organization/logo', { method: 'DELETE' });
      onLogoChange(null);
      setLogoMsg('Logo removed.');
    } catch (err) {
      setLogoError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="stack" style={{ gap: '1.25rem' }}>
      <div>
        <h3 style={{ marginTop: 0 }}>Portal appearance</h3>
        <p className="muted small">
          Customize how your client portal looks. Changes apply to the client portal and your agency workspace.
        </p>
      </div>

      <div>
        <span className="field-label">Logo &amp; favicon</span>
        <p className="muted small" style={{ margin: '0.25rem 0 0.5rem' }}>
          Upload your agency logo. The same image is used as the browser tab icon (favicon).
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,image/x-icon,.ico"
            disabled={uploading}
            onChange={(e) => void uploadLogo(e.target.files?.[0])}
          />
          {logoUrl ? (
            <button type="button" className="btn-secondary btn-sm" disabled={uploading} onClick={() => void removeLogo()}>
              Remove logo
            </button>
          ) : null}
        </div>
        {logoUrl ? (
          <div className="branding-logo-preview">
            <img src={logoUrl} alt="Agency logo preview" />
          </div>
        ) : null}
        {logoMsg ? <p className="muted small">{logoMsg}</p> : null}
        {logoError ? <p className="err">{logoError}</p> : null}
      </div>

      <div>
        <span className="field-label">Button style</span>
        <p className="muted small" style={{ margin: '0.25rem 0 0.65rem' }}>
          Choose one of 10 preset button sets. Each set defines colors and shape for all button types.
        </p>
        <div className="branding-preset-grid">
          {BUTTON_PRESETS.map((preset) => {
            const selected = (form.button_preset || 'classic') === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                className={`branding-preset-card${selected ? ' is-selected' : ''}`}
                onClick={() => setForm((prev) => ({ ...prev, button_preset: preset.id }))}
              >
                <p className="branding-preset-card__title">{preset.label}</p>
                <p className="branding-preset-card__desc">{preset.description}</p>
                <ButtonPresetPreview preset={preset.id} compact />
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="field-label">Colors</span>
        <div className="branding-color-grid" style={{ marginTop: '0.65rem' }}>
          {[
            ['header_bg_color', 'Header background'],
            ['content_bg_color', 'Content background'],
            ['header_text_color', 'Header text'],
            ['content_text_color', 'Content text'],
            ['link_color', 'Links'],
          ].map(([key, label]) => (
            <label key={key} className="field branding-color-field">
              <input
                type="color"
                value={form[key] || DEFAULT_BRANDING_COLORS[key]}
                onChange={(e) => setColor(key, e.target.value)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
